import { useEffect, useState } from 'react';
import type { FC, ElementType } from 'react';
import { useLocation, matchPath, Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Box, Divider, Drawer, IconButton, List, ListItem, ListItemText, Popover, Button, Typography, Chip } from '@material-ui/core';
import { projectUpdateActive, projectGetAll } from '../api/project';
import { DashboardSidebarItem } from './dashboard-sidebar-item';
import { IpopStepper, IpopStep } from './ipop-stepper';
import { Scrollbar } from './scrollbar';
import { ChevronLeft as ChevronLeftIcon } from '../icons/chevron-left';
import { ChevronRight as ChevronRightIcon } from '../icons/chevron-right';
import { Cog as CogIcon } from '../icons/cog';
import { ViewList as ViewListIcon } from '../icons/view-list';
import { Download as DownloadIcon } from '../icons/download';
import { useSelector, RootState } from '../store';
import { useAuth } from '../hooks/use-auth';
import { useSettings } from '../contexts/settings-context';
import { review1GetCustomers, review1GetCompanies, review1GetSolution } from '../slices/review1Slice';
import { review2GetAll } from '../slices/review2Slice';
import { review3GetAll } from '../slices/review3Slice';
import { review4GetAll } from '../slices/review4Slice';
import { openBugReportDialog, closeBugReportDialog } from '../slices/bugReportSlice';
import { BugReportDialog } from '../components/quick-actions/bug-report-dialog';
import { useDispatch } from "react-redux";
import { IconProp, IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faBug } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Dashboard as DashboardIcon } from '../icons/dashboard';
import { faGear, faQuestion, faSquareCheck, faGaugeHigh, faStickyNote, faEnvelope, faFolder, faChevronDown, faComments, faListUl } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from 'react-i18next';

interface DashboardSidebarProps {
  onPin: () => void;
  pinned: boolean;
}

interface Item {
  href?: string;
  external?: boolean;
  icon?: IconProp;
  items?: Array<{ href: string; title: string; confidenceIndice: number; }>
  title: string;
  confidenceIndice?: number;
  ipopLetter?: string;
  completion?: number;
  isGroupLabel?: boolean;
  badge?: { count: number; color?: 'error' | 'warning' | 'primary' | 'info' | 'success' };
}

// const items: Item[] = [
//   {
//     icon: ChartPieIcon,
//     title: 'Reports',
//     items: [
//       {
//         href: '/reports',
//         title: 'Overview'
//       },
//       {
//         href: '/reports/sales',
//         title: 'Sales'
//       }
//     ]
//   },
//   {
//     icon: CogIcon,
//     title: 'Account',
//     items: [
//       {
//         href: '/account',
//         title: 'General Settings'
//       },
//       {
//         href: '/account/notifications',
//         title: 'Notifications'
//       }
//     ]
//   },
//   {
//     icon: OfficeBuildingIcon,
//     title: 'Organization',
//     items: [
//       {
//         href: '/organization',
//         title: 'General Settings'
//       },
//       {
//         href: '/organization/team',
//         title: 'Team'
//       },
//       {
//         href: '/organization/billing',
//         title: 'Billing'
//       }
//     ]
//   }
// ];

export const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
  const { onPin, pinned } = props;
  const { pathname } = useLocation();
  const [openedItem, setOpenedItem] = useState<Item | null>(null);
  const [activeItem, setActiveItem] = useState<Item | null>(null);
  const [activeHref, setActiveHref] = useState('');
  const [hovered, setHovered] = useState(false);
  const { accessRights, activeProject, projects } = useAuth() as any;
  const { settings, saveSettings } = useSettings();
  const { t } = useTranslation();

  // Project switcher popover (opened from the sidebar project card).
  const [projectAnchor, setProjectAnchor] = useState<HTMLElement | null>(null);
  // Fetch the project list from the same endpoint as the /projects page so the
  // switcher always matches it (the auth-context `projects` is loaded once at
  // login and can go stale, e.g. after creating a project).
  const [freshProjects, setFreshProjects] = useState<any[] | null>(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await projectGetAll({});
      if (!cancelled && Array.isArray(res)) {
        setFreshProjects(res);
      }
    })();
    return () => { cancelled = true; };
  }, []);
  // Prefer the freshly-fetched list; fall back to the auth-context projects.
  const projectList = (freshProjects && freshProjects.length > 0) ? freshProjects : projects;
  const handleProjectChange = async (id: string) => {
    setProjectAnchor(null);
    const status = await projectUpdateActive({ id });
    if (status === 'success') {
      window.location.href = '/';
    }
  };

  // Dynamic elements
  // Review 1
  const { customers, companies, companyFit, solution } = useSelector((state: RootState) => state.review1);
  const [review1ConfidenceIndice, setReview1ConfidenceIndice] = useState(1);

  useEffect(() => {
    let review1CI = customers.data.map(c => c['confidence_indice'])
    // review1CI.push(companyFit.confidence_indice)
    review1CI = review1CI.concat(companies.data.map(c => c['confidence_indice']));
    console.log("review1CI", review1CI);
    if (review1CI.length > 0) {
      let newConfidenceIndice = Math.min(...review1CI);
      setReview1ConfidenceIndice(newConfidenceIndice);
    };
  }, [customers, companies])

  // Review 2
  const { review2 } = useSelector((state: RootState) => state.review2);

  // Review 3
  const { review3 } = useSelector((state: RootState) => state.review3);

  // Review 4
  const { review4 } = useSelector((state: RootState) => state.review4);

  const dispatch = useDispatch();
  const { bugReportDialogOpen } = useSelector((state: RootState) => state.bugReport);

  // Load every review's data once so the sidebar progress bars reflect the real
  // confidence indices, even on pages (e.g. dashboard) that don't fetch them.
  // Without this the reviews keep their default state and every bar shows 20%.
  useEffect(() => {
    dispatch(review1GetCustomers());
    dispatch(review1GetCompanies());
    dispatch(review1GetSolution());
    dispatch(review2GetAll());
    dispatch(review3GetAll());
    dispatch(review4GetAll());
  }, []);

  // Average of a review's real sub-confidence indices (1-5). Using the average
  // — rather than the stored confidence_indice, which is a Math.min and stays at
  // 1 (20%) as soon as a single sub-item is untouched — gives a progress value
  // that actually reflects how far the review has been filled in. Missing values
  // are ignored; 0 when there is no data yet.
  const avgCI = (vals: any[]) => {
    const nums = vals.filter((v) => typeof v === 'number' && !Number.isNaN(v) && v > 0);
    if (!nums.length) return 0;
    return nums.reduce((a, b) => a + b, 0) / nums.length;
  };
  const review1Avg = avgCI([
    ...customers.data.map((c) => c['confidence_indice']),
    ...companies.data.map((c) => c['confidence_indice']),
  ]);
  const review2Avg = review2 ? avgCI([
    review2.activity_chain?.confidence_indice,
    review2.definition?.kiss_confidence_indice,
    review2.definition?.terminators_confidence_indice,
    review2.economic_model?.confidence_indice,
    review2.skill?.confidence_indice,
    review2.benefit?.confidence_indice,
  ]) : 0;
  const review3Avg = review3 ? avgCI([
    review3.factors_confidence_indice,
    review3.stakeholders_confidence_indice,
  ]) : 0;
  const review4Avg = review4 ? avgCI([
    review4.moves_confidence_indice,
    review4.ips_confidence_indice,
    review4.timeline_confidence_indice,
    review4.budget_confidence_indice,
  ]) : 0;

  const localItems: Item[] = []
  // IPOP progress stepper: one bar per accessible review, filled to its confidence level.
  const ipopSteps: IpopStep[] = []

  if (accessRights) {
    const reviewMax = Number(activeProject?.review_max) || 4;

    localItems.push({
      icon: faGaugeHigh as IconProp,
      title: 'Project Dashboard',
      href: '/'
    })

    localItems.push({
      icon: faComments as IconProp,
      title: 'Assistant',
      href: '/assistant'
    })

    localItems.push({ title: 'IpOp Reviews', isGroupLabel: true })

    if ((accessRights['review_1'] === 1 || accessRights['review_1'] === 2) && reviewMax >= 1) {
      ipopSteps.push({ key: '1', label: 'Hope', level: review1Avg, pathPrefix: '/review-1', href: '/review-1/customers' });
    }
    if ((accessRights['review_2'] === 1 || accessRights['review_2'] === 2) && reviewMax >= 2) {
      ipopSteps.push({ key: '2', label: 'Worth it', level: review2Avg, pathPrefix: '/review-2', href: '/review-2/definition-of-success' });
    }
    if ((accessRights['review_3'] === 1 || accessRights['review_3'] === 2) && reviewMax >= 3) {
      ipopSteps.push({ key: '3', label: 'Influencers', level: review3Avg, pathPrefix: '/review-3', href: '/review-3/factors' });
    }
    if ((accessRights['review_4'] === 1 || accessRights['review_4'] === 2) && reviewMax >= 4) {
      ipopSteps.push({ key: '4', label: 'How', level: review4Avg, pathPrefix: '/review-4', href: '/review-4/moves' });
    }

    if ((accessRights['review_1'] === 1 || accessRights['review_1'] === 2) && reviewMax >= 1) {
      let customerItems = [
        {
          href: '/review-1/customers',
          title: 'customers',
          confidenceIndice: null,
          isChild: false
        },
      ]

      customers.data.forEach(c => {
        customerItems.push(
          {
            href: `/review-1/customer-fit/${c['id']}`,
            title: `${c['name']}`,
            confidenceIndice: c['confidence_indice'],
            isChild: true
          }
        )
      });

      customerItems.push(
        {
          // The Solution carries its own confidence index (one per project),
          // unlike the Customers/Company fit rows which are just list pages.
          href: '/review-1/solutions',
          title: 'solution',
          confidenceIndice: solution?.confidence_indice ?? null,
          isChild: false
        }
      );

      customerItems.push(
        {
          href: '/review-1/companies',
          title: 'company_fit',
          confidenceIndice: null,
          isChild: false
        }
      );

      companies.data.forEach(c => {
        customerItems.push(
          {
            href: `/review-1/company-fit/${c['id']}`,
            title: `${c['name']}`,
            confidenceIndice: c['confidence_indice'],
            isChild: true
          }
        )
      });

      localItems.push(
        {
          title: 'review_1',
          confidenceIndice: review1ConfidenceIndice ? review1ConfidenceIndice : 1,
          ipopLetter: '1',
          completion: (review1Avg / 5) * 100,
          items: customerItems
        }
      )
    }

    if ((accessRights['review_2'] === 1 || accessRights['review_2'] === 2) && reviewMax >= 2) {
      localItems.push(
        {
          // icon: OfficeBuildingIcon,
          title: 'review_2',
          href: '/review-2/definition-of-success',
          confidenceIndice: review2?.confidence_indice,
          ipopLetter: '2',
          completion: (review2Avg / 5) * 100,
        }
      )
    }

    if ((accessRights['review_3'] === 1 || accessRights['review_3'] === 2) && reviewMax >= 3) {
      localItems.push(
        {
          // icon: OfficeBuildingIcon,
          title: 'review_3',
          href: '/review-3/factors',
          confidenceIndice: review3?.confidence_indice,
          ipopLetter: '3',
          completion: (review3Avg / 5) * 100,
        }
      )
    }

    if ((accessRights['review_4'] === 1 || accessRights['review_4'] === 2) && reviewMax >= 4) {
      localItems.push(
        {
          // icon: OfficeBuildingIcon,
          title: 'review_4',
          href: '/review-4/moves',
          confidenceIndice: review4?.confidence_indice,
          ipopLetter: '4',
          completion: (review4Avg / 5) * 100,
        }
      )
    }

    // All project — Lists, Validations, Reports and Project settings grouped
    // under a single section header. (Lists is one entry; its page has a tab per
    // list: unknowns, actions, factors, stakeholders, comments, hidden, empty.)
    const canSeeProjectSection =
      accessRights['review_4'] === 1 || accessRights['review_4'] === 2 || accessRights['project_admin_space'] === 1;

    if (canSeeProjectSection) {
      localItems.push({ title: 'All project', isGroupLabel: true })
    }

    if (accessRights['review_4'] === 1 || accessRights['review_4'] === 2) {
      localItems.push(
        {
          icon: faListUl as IconProp,
          title: 'Lists',
          href: '/lists'
        },
        {
          icon: faSquareCheck as IconProp,
          title: 'validations',
          href: '/validations'
        },
        {
          icon: faStickyNote as IconProp,
          title: 'reports',
          href: '/reports',
        }
      )
    }

    if (accessRights['project_admin_space'] === 1) {
      localItems.push(
        {
          icon: faGear as IconProp,
          title: 'project_settings',
          href: '/project'
        }
      )
    }

  }

  const handleOpenItem = (item: Item): void => {
    if (openedItem === item) {
      setOpenedItem(null);
      return;
    }

    setOpenedItem(item);
  };

  // Highlight the sidebar item for the current route. Match by section (the
  // /review-N or first path segment) rather than the exact href, so sub-pages
  // like /review-2/activity-chain still activate their review — and always
  // recompute (resetting when nothing matches) so the highlight never sticks on
  // a stale item, e.g. staying on "Project Dashboard" after navigating away.
  useEffect(() => {
    const inSection = (href?: string): boolean => {
      if (!href) return false;
      if (href === '/') return pathname === '/';
      const section = '/' + href.split('/')[1];
      return pathname === section || pathname.startsWith(section + '/');
    };

    let matchedItem: Item | null = null;
    let matchedHref = '';

    for (const item of localItems) {
      if (item.isGroupLabel) continue;

      if (item.items) {
        // Prefer an exact sub-item match (drives the sub-item highlight); fall
        // back to the section so any /review-1/* page still activates the parent.
        const exact = item.items.find((sub) => matchPath({ path: sub.href, end: true }, pathname));
        if (exact) {
          matchedItem = item;
          matchedHref = exact.href;
          break;
        }
        if (item.items.some((sub) => inSection(sub.href))) {
          matchedItem = item;
          break;
        }
      } else if (inSection(item.href)) {
        matchedItem = item;
        break;
      }
    }

    setActiveItem(matchedItem);
    setActiveHref(matchedHref);
    if (matchedItem) {
      setOpenedItem(matchedItem);
    }
  }, [pathname, accessRights]);

  return (
    <Drawer
      open
      sx={{ zIndex: 1000, border: 'none' }}
      variant="permanent"
      elevation={7}
      PaperProps={{
        onMouseOver: () => { setHovered(true); },
        onMouseLeave: () => { setHovered(false); },
        sx: {
          backgroundColor: 'background.default',
          border: 'none',
          boxShadow: '0px 8px 12px rgba(9, 30, 66, 0.08)',
          // height: 'calc(100% - 64px)',
          height: 'calc(100%)',
          overflowX: 'hidden',
          // top: 64,
          transition: 'width 250ms ease-in-out',
          width: pinned ? 250 : 73,
          '& .simplebar-content': {
            height: '100%'
          },
          '&:hover': {
            width: 250,
            '& span, p': {
              display: 'flex'
            }
          }
        }
      }}
    >
      <Scrollbar
      // style={{
      //   // display: 'flex',
      //   // flex: 1,
      //   overflowX: 'hidden',
      //   overflowY: 'scroll'
      // }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            p: 2
          }}
        >
          {/* <List disablePadding>
            {activeItem && (
              localItems.map((item) => (
                <DashboardSidebarItem
                  active={activeItem?.title === item.title}
                  activeHref={activeHref}
                  key={item.title}
                  onOpen={() => handleOpenItem(item)}
                  open={openedItem?.title === item.title && (hovered || pinned)}
                  pinned={pinned}
                  {...item}
                />
              ))
            )}
          </List> */}
          <img src={`/static/ipop-logo-${settings.theme}.png`} width={'100%'} />

          {(hovered || pinned) && activeProject?.name && (
            <Box
              onClick={(e) => setProjectAnchor(e.currentTarget)}
              sx={{
                mt: 2,
                p: 1.25,
                border: 1,
                borderColor: 'divider',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                cursor: 'pointer',
                transition: 'border-color .12s, background-color .12s',
                '&:hover': { borderColor: 'neutral.400', backgroundColor: 'action.hover' },
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: 'primary.light',
                  color: 'primary.main',
                  display: 'grid',
                  placeItems: 'center',
                  flexShrink: 0,
                }}
              >
                <FontAwesomeIcon icon={faFolder as IconProp} size="xs" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700, color: 'text.disabled' }}>
                  Project
                </Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {activeProject.name}
                </Typography>
              </Box>
              <FontAwesomeIcon icon={faChevronDown as IconProp} size="xs" style={{ opacity: 0.5 }} />
            </Box>
          )}

          <Popover
            anchorEl={projectAnchor}
            open={Boolean(projectAnchor)}
            onClose={() => setProjectAnchor(null)}
            anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
            PaperProps={{ sx: { width: 250, mt: 0.5, backgroundColor: 'background.default' } }}
          >
            <List dense>
              {Array.isArray(projectList) && projectList.length > 0 ? (
                projectList.map((project) => (
                  <ListItem
                    key={project.id}
                    button
                    selected={project.id === activeProject?.id}
                    onClick={() => handleProjectChange(project.id)}
                    sx={{
                      '&.Mui-selected': { backgroundColor: 'primary.main', color: 'primary.contrastText' },
                      '&.Mui-selected:hover': { backgroundColor: 'primary.dark' },
                    }}
                  >
                    <ListItemText
                      primary={project?.name || (project?.id ? `IPOP Project - ${project.id.substring(0, 6)}` : 'Unnamed Project')}
                      primaryTypographyProps={{ noWrap: true, fontSize: 13 }}
                    />
                  </ListItem>
                ))
              ) : (
                <ListItem>
                  <ListItemText primary={t('no_projects') || 'No projects'} />
                </ListItem>
              )}
              <Divider />
              <ListItem button component={RouterLink} to="/projects" onClick={() => setProjectAnchor(null)}>
                <ListItemText primary={t('view_all') || 'View all'} primaryTypographyProps={{ fontSize: 13, fontWeight: 600 }} />
              </ListItem>
              <ListItem sx={{ pt: 1 }}>
                <Button
                  fullWidth
                  variant="contained"
                  size="small"
                  component={RouterLink}
                  to="/projects/new"
                  onClick={() => setProjectAnchor(null)}
                >
                  {t('new_project') || 'Create new project'}
                </Button>
              </ListItem>
            </List>
          </Popover>

          <Divider style={{ marginTop: 16, marginBottom: 16 }} />

          <IpopStepper steps={ipopSteps} />

          <List disablePadding>
            {localItems.map((item) => (
              <DashboardSidebarItem
                active={activeItem?.title === item.title}
                activeHref={activeHref}
                key={item.title}
                onOpen={() => handleOpenItem(item)}
                open={openedItem?.title === item.title && (hovered || pinned)}
                pinned={pinned}
                {...item}
              />
            ))
            }
          </List>
          {/* <Box sx={{ flexGrow: 1 }} />
          <Divider />
          <Box sx={{ pt: 1 }}>
            <IconButton onClick={onPin}>
              {pinned ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>
            <IconButton onClick={() => dispatch(openBugReportDialog({element: {}, isEditabled: true, isDuplicated: false}))}>
              <FontAwesomeIcon icon={faBug as IconProp} size={"xs"} />
            </IconButton>
          </Box> */}
        </Box>
      </Scrollbar>
      <BugReportDialog open={bugReportDialogOpen} onClose={() => dispatch(closeBugReportDialog())} />
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onPin: PropTypes.func,
  pinned: PropTypes.bool
};
